import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronDown, Loader2, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { SiteFooter, SiteHeader } from "@/components/site/SiteChrome";
import { AdSlots } from "@/components/site/AdSlot";
import { errorMessage } from "@/lib/api";
import { getSupport, sendContact } from "@/lib/site";
import { useDocumentMeta, useSite } from "@/hooks/useSite";
import { currentLanguage, t } from "@/i18n";

/* The written answers, before any chatbot. A page is indexed by search engines
   and a conversation is not, so this is where the knowledge lives — and later,
   what a bot will be allowed to recite. */

const Entry = ({ item, open, onToggle }) => (
  <div className="border-b" style={{ borderColor: "var(--line)" }}>
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      data-testid={`faq-${item.id}`}
      className="flex w-full items-start justify-between gap-4 py-5 text-left"
    >
      <span className="text-[17px] font-medium leading-snug">{item.question}</span>
      <ChevronDown
        className="mt-1 h-4 w-4 shrink-0 transition-transform"
        style={{
          color: "var(--muted-ink)",
          transform: open ? "rotate(180deg)" : "none",
        }}
      />
    </button>
    {open ? (
      <p
        className="max-w-2xl pb-5 text-[14.5px] leading-relaxed"
        style={{ color: "var(--ink-soft)" }}
      >
        {item.answer}
      </p>
    ) : null}
  </div>
);

const ContactForm = ({ support }) => {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    if (form.message.trim().length < 5) {
      toast.error(t("help.tooShort"));
      return;
    }
    setBusy(true);
    try {
      await sendContact({
        ...form,
        lang: currentLanguage(),
        page: window.location.pathname,
      });
      setDone(true);
      toast.success(t("help.sent"));
    } catch (error) {
      toast.error(errorMessage(error, t("help.failed")));
    } finally {
      setBusy(false);
    }
  };

  const set = (key) => (event) => setForm({ ...form, [key]: event.target.value });

  return (
    <section className="surface mt-16 rounded-xl p-6" data-testid="contact-form">
      <h2 className="text-2xl font-semibold">{t("help.ask")}</h2>
      {support?.contact_intro ? (
        <p className="mt-2 max-w-2xl text-[13.5px] leading-relaxed text-[color:var(--ink-soft)]">
          {support.contact_intro}
        </p>
      ) : null}

      {done ? (
        <p className="mt-5 text-[14px]" style={{ color: "var(--success)" }} data-testid="contact-sent">
          {t("help.sent")}
        </p>
      ) : (
        <form onSubmit={submit} className="mt-5">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Input
              value={form.name}
              onChange={set("name")}
              placeholder={t("help.name")}
              data-testid="contact-name"
              className="h-11"
            />
            <Input
              type="email"
              value={form.email}
              onChange={set("email")}
              placeholder={t("help.email")}
              data-testid="contact-email"
              className="h-11"
            />
          </div>
          <Input
            value={form.subject}
            onChange={set("subject")}
            placeholder={t("help.subject")}
            data-testid="contact-subject"
            className="mt-3 h-11"
          />
          <Textarea
            value={form.message}
            onChange={set("message")}
            placeholder={t("help.message")}
            rows={6}
            data-testid="contact-message"
            className="mt-3"
          />
          <div className="mt-4 flex flex-wrap items-center gap-4">
            <Button type="submit" disabled={busy} data-testid="contact-send">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {t("help.send")}
            </Button>
            {/* The form lands in a database the owner has to come and read, so
                the address is printed too: a slower promise, plainly kept. */}
            {support?.contact_email ? (
              <span className="flex items-center gap-1.5 text-[12.5px] text-[color:var(--muted-ink)]">
                <Mail className="h-3.5 w-3.5" />
                {t("help.orEmail")}{" "}
                <a href={`mailto:${support.contact_email}`} className="link-underline">
                  {support.contact_email}
                </a>
              </span>
            ) : null}
          </div>
        </form>
      )}
    </section>
  );
};

export default function Support() {
  const site = useSite();
  const lang = currentLanguage();
  const [support, setSupport] = useState(null);
  const [schema, setSchema] = useState(null);
  const [open, setOpen] = useState(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    setFailed(false);
    getSupport(lang)
      .then((data) => {
        setSupport(data.support);
        setSchema(data.schema);
      })
      // an empty page and an unreachable server look identical to a visitor
      // unless we say which one it is — and only one of them is worth waiting on
      .catch(() => {
        setSupport({ faq: [] });
        setFailed(true);
      });
  }, [lang, attempt]);

  useDocumentMeta({
    title: `${support?.title || t("nav.help")} — ${site?.brand?.name || t("app.name")}`,
    description: support?.intro,
    url: typeof window !== "undefined" ? window.location.href : "",
  });

  // Structured data, so the answers can appear directly in search results.
  // Injected rather than rendered: it is data for crawlers, not content.
  useEffect(() => {
    if (!schema?.mainEntity?.length) return undefined;
    const tag = document.createElement("script");
    tag.type = "application/ld+json";
    tag.text = JSON.stringify(schema);
    document.head.appendChild(tag);
    return () => tag.remove();
  }, [schema]);

  const entries = support?.faq || [];

  return (
    <div className="min-h-screen">
      <div className="hero-wash">
        <SiteHeader brandName={site?.brand?.name} tagline={site?.brand?.tagline} />
        <div className="mx-auto max-w-[820px] px-6 pb-12 pt-12">
          <p className="eyebrow">{t("nav.help")}</p>
          <h1 className="mt-3 text-4xl font-semibold leading-[1.08] tracking-tight sm:text-5xl">
            {support?.title || t("nav.help")}
          </h1>
          {support?.intro ? (
            <p className="mt-4 max-w-2xl text-[16px] leading-relaxed text-[color:var(--ink-soft)]">
              {support.intro}
            </p>
          ) : null}
        </div>
      </div>

      <main className="mx-auto max-w-[820px] px-6 py-12">
        {support === null ? (
          <p className="flex items-center gap-2 text-[13.5px] text-[color:var(--muted-ink)]">
            <Loader2 className="h-4 w-4 animate-spin" /> {t("site.loading")}
          </p>
        ) : entries.length ? (
          <div data-testid="faq-list">
            {entries.map((item) => (
              <Entry
                key={item.id}
                item={item}
                open={open === item.id}
                onToggle={() => setOpen(open === item.id ? null : item.id)}
              />
            ))}
          </div>
        ) : failed ? (
          <div data-testid="faq-offline">
            <p className="text-[14px]" style={{ color: "var(--warning)" }}>
              {t("site.offline")}
            </p>
            <Button
              variant="outline"
              onClick={() => setAttempt((n) => n + 1)}
              className="mt-3"
              data-testid="faq-retry"
            >
              {t("site.retry")}
            </Button>
          </div>
        ) : (
          <p className="text-[14px] text-[color:var(--ink-soft)]" data-testid="faq-empty">
            {t("help.empty")}
          </p>
        )}

        <ContactForm support={support} />

        <AdSlots site={site} placement="footer" />
      </main>

      <SiteFooter site={site} />
    </div>
  );
}
